import {
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {DataService} from '../services/data.service';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {ExcelService} from '../services/excel.service';
import {DialogCarComponent} from '../dialog-car/dialog-car.component';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {finalize, tap} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {TableSortState} from '../table-sort';

@Component({
    selector: 'app-cars',
    templateUrl: './cars.component.html',
    styleUrls: ['./cars.component.css']
})
export class CarsComponent implements OnInit, OnDestroy {

    @ViewChild('dialogsContainer', {read: ViewContainerRef, static: true}) dialogsContainer;
    dialogRef: ComponentRef<DialogCarComponent>;

    carsData: Array<any> = [];
    filteredCarsData: Array<any> = [];
    pageSize = 25;
    currentPage = 1;
    tableSort = new TableSortState();
    isLoadingCars = true;
    isCarsRequestInProgress = false;
    searchText: string;
    cityFilter = '';
    companyFilter = '';
    suspendedFilter = '';
    canExportData = false;
    dataChangedCarsSubscription: Subscription;
    dataChangedSessionSubscription: Subscription;

    constructor(private dataService: DataService,
                private restService: RestService,
                private uiBlockerService: UIBlockerService,
                private excelService: ExcelService,
                private resolver: ComponentFactoryResolver) { /* noop */
    }

    ngOnInit() {
        this.setCarsData(this.dataService.getCars());
        this.dataChangedCarsSubscription = this.dataService.dataChangedCars.subscribe(() => {

            this.setCarsData(this.dataService.getCars());
        });
        this.checkIfUserCanExport();
        this.dataChangedSessionSubscription = this.dataService.dataChangedSession.subscribe(() => {

            this.checkIfUserCanExport();
            this.getCarsData();
        });
        this.getCarsData();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.dataChangedCarsSubscription)) {
            this.dataChangedCarsSubscription.unsubscribe();
        }

        if (!isNullOrUndefined(this.dataChangedSessionSubscription)) {
            this.dataChangedSessionSubscription.unsubscribe();
        }
    }

    getCarsData(showLoading: boolean = true) {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier()) || this.isCarsRequestInProgress) {
            return;
        }

        this.isCarsRequestInProgress = true;
        if (showLoading) {
            this.isLoadingCars = true;
        }
        this.restService.getCars()
            .pipe(
                finalize(() => {
                    this.isCarsRequestInProgress = false;
                    if (showLoading) {
                        this.isLoadingCars = false;
                    }
                    this.uiBlockerService.unBlockUI();
                }),
                tap(
                    () => {
                        /* noop */
                    },
                    () => {
                        /* noop */
                    }
                )
            )
            .subscribe((res: any) => {
                if (this.dataService.isResponseSuccessful(res)) {
                    this.dataService.setCars(res.Cars);
                } else {
                    // maybe show notification with error getting data
                }
            });
    }

    setCarsData(cars: Array<any>) {
        this.carsData = isNullOrUndefined(cars) ? [] : cars;
        this.updateFilteredCars(false);
    }

    onSearchTextChanged(searchText: string) {
        this.searchText = searchText;
        this.updateFilteredCars(true);
    }

    updateFilteredCars(resetPage: boolean) {
        const searchText = isNullOrUndefined(this.searchText) ? '' : this.searchText.toLowerCase();
        this.filteredCarsData = this.carsData.filter(car =>
            (searchText === '' || this.isCarMatchingSearch(car, searchText))
            && (this.cityFilter === '' || car.CityName === this.cityFilter)
            && (this.companyFilter === '' || car.CompanyName === this.companyFilter)
            && (this.suspendedFilter === ''
                || String(this.isCarSuspended(car)) === this.suspendedFilter)
        );

        if (resetPage) {
            this.currentPage = 1;
        }

        this.clampCurrentPage();
    }

    onCityFilterChanged(value: string) {
        this.cityFilter = value;
        this.updateFilteredCars(true);
    }

    onCompanyFilterChanged(value: string) {
        this.companyFilter = value;
        this.updateFilteredCars(true);
    }

    onSuspendedFilterChanged(value: string) {
        this.suspendedFilter = value;
        this.updateFilteredCars(true);
    }

    getCityOptions() {
        return this.getDistinctOptions(this.carsData, car => car.CityName);
    }

    getCompanyOptions() {
        return this.getDistinctOptions(this.carsData, car => car.CompanyName);
    }

    isCarSuspended(car: any) {
        return !isNullOrUndefined(car.Suspension) && Boolean(car.Suspension.IsSuspended);
    }

    isCarMatchingSearch(car: any, searchText: string): boolean {
        if (!isNullOrUndefined(car.CarId) && String(car.CarId).includes(searchText)) {
            return true;
        }

        if (!isNullOrUndefined(car.CarNumber) && car.CarNumber.toLowerCase().includes(searchText)) {
            return true;
        }

        if (!isNullOrUndefined(car.Drivers) && car.Drivers.length > 0) {
            for (const driver of car.Drivers) {
                if (!isNullOrUndefined(driver.DriverName) && driver.DriverName.toLowerCase().includes(searchText)) {
                    return true;
                }
            }
        }

        if (searchText.length > 4 && !isNullOrUndefined(car.DeviceId) && car.DeviceId.includes(searchText)) {
            return true;
        }

        return false;
    }

    getPagedCars() {
        const sortedCars = this.tableSort.sort(
            this.filteredCarsData,
            (car, column) => this.getCarSortValue(car, column)
        );
        return sortedCars.slice(this.getPageStartIndex(), this.getPageEndIndex());
    }

    getPageStartIndex(): number {
        return (this.currentPage - 1) * this.pageSize;
    }

    getPageEndIndex(): number {
        return this.getPageStartIndex() + this.pageSize;
    }

    getTotalPages(): number {
        return Math.max(1, Math.ceil(this.filteredCarsData.length / this.pageSize));
    }

    getVisibleRangeStart(): number {
        return this.filteredCarsData.length === 0 ? 0 : this.getPageStartIndex() + 1;
    }

    getVisibleRangeEnd(): number {
        return Math.min(this.getPageEndIndex(), this.filteredCarsData.length);
    }

    goToPreviousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
        }
    }

    goToNextPage() {
        if (this.currentPage < this.getTotalPages()) {
            this.currentPage++;
        }
    }

    changePageSize() {
        this.currentPage = 1;
        this.clampCurrentPage();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.getTotalPages()) {
            this.currentPage = page;
        }
    }

    getVisiblePages() {
        const totalPages = this.getTotalPages();
        const start = Math.max(1, Math.min(this.currentPage - 2, totalPages - 4));
        const end = Math.min(totalPages, start + 4);
        const pages = [];
        for (let page = start; page <= end; page++) {
            pages.push(page);
        }
        return pages;
    }

    clampCurrentPage() {
        if (this.currentPage > this.getTotalPages()) {
            this.currentPage = this.getTotalPages();
        }

        if (this.currentPage < 1) {
            this.currentPage = 1;
        }
    }

    private getCarSortValue(car: any, column: string) {
        switch (column) {
            case 'carId':
                return car.CarId;
            case 'carNumber':
                return car.CarNumber;
            case 'city':
                return car.CityName;
            case 'company':
                return car.CompanyName;
            case 'lastLogin':
                return this.getTimeValue(car.LastLoginDate_iso || car.LastLoginDate);
            case 'suspended':
                return !isNullOrUndefined(car.Suspension) && Boolean(car.Suspension.IsSuspended);
            case 'drivers':
                return this.concatDriverNames(car.Drivers);
            default:
                return '';
        }
    }

    private getTimeValue(value: any) {
        if (isNullOrUndefined(value) || value === '') {
            return undefined;
        }
        const time = new Date(value).getTime();
        return isNaN(time) ? undefined : time;
    }

    private getDistinctOptions(items: Array<any>, valueGetter: (item: any) => any) {
        const values = items
            .map(valueGetter)
            .filter(value => !isNullOrUndefined(value) && String(value).trim() !== '')
            .map(value => String(value));
        return values.filter((value, index) => values.indexOf(value) === index)
            .sort((left, right) => left.localeCompare(right));
    }

    checkIfUserCanExport() {
        const userData = this.dataService.getUserInfo();
        if (!isNullOrUndefined(userData) && !isNullOrUndefined(userData.Permissions)) {
            for (const permission of userData.Permissions) {
                if (permission.toLowerCase() === 'canexportdata') {
                    this.canExportData = true;
                }
            }
        }
    }

    exportData(): void {
        // before we actually export we will make a new structure of data
        const dataForExport = [];
        for (const car of this.carsData) {
            const dataToAdd = {
                CarId: car.CarId,
                CarNumber: car.CarNumber,
                LastLogin: car.LastLoginAsString,
                Drivers: this.concatDriverNames(car.Drivers)
            };
            dataForExport.push(dataToAdd);
        }
        this.excelService.exportAsExcelFile(dataForExport, 'Cars');
    }

    concatDriverNames(drivers: any[]) {
        if (!isNullOrUndefined(drivers) && drivers.length > 0) {
            let driversConcat = '';
            for (const driver of drivers) {
                driversConcat += driver.DriverName + ';';
            }
            return driversConcat;
        } else {
            return '';
        }
    }

    showCarDetails(carItem: string) {
        this.showDialogForCar(carItem);
    }

    showDialogForCar(carItem: string) {
        const dialogTexts: any = {
            HeaderText: 'Car Details',
            carItem,
            isTest: false
        };
        // Create Dialog Component:
        this.dialogsContainer.clear();
        const factory: ComponentFactory<DialogCarComponent> = this.resolver

            .resolveComponentFactory(DialogCarComponent);
        this.dialogRef = this.dialogsContainer.createComponent(factory);

        // Customize with texts:
        this.dialogRef.instance.headerText = dialogTexts.HeaderText;
        this.dialogRef.instance.carItem = dialogTexts.carItem;

        // Listen for Dialog Events (Cancel or Confirm):
        this.dialogRef.instance.onDialogConfirm.subscribe(() => {

            // for now no action needed
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onDialogCancel.subscribe(() => {

            // for now no action needed
            this.dialogRef.destroy();
        });
        this.dialogRef.instance.onCarUpdated.subscribe(() => {

            this.getCarsData(false);
        });
    }

}
